// DeferredActionLogList.jsx
import React, { useMemo } from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SmartToyIcon from "@mui/icons-material/SmartToy";

/**
 * @param {Object[]} logs - список логов
 * @param {number}   logs[].id
 * @param {"SUCCESS"|"CHECK_CONDITION_FAILED"|"FAILED"|"AI_EXECUTE"} logs[].status
 * @param {string}   logs[].error
 * @param {string}   logs[].createdAt - ISO datetime
 * @param {Object}   logs[].user
 */
export default function DeferredActionLogList({ logs = [] }) {
    const STATUSES = ["SUCCESS", "CHECK_CONDITION_FAILED", "FAILED", "AI_EXECUTE"];

    const fmtDate = (iso) => {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso || "—";
        }
    };

    const statusChip = (status) => {
        switch (status) {
            case "SUCCESS":
                return (
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                        label="SUCCESS"
                    />
                );
            case "CHECK_CONDITION_FAILED":
                return (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<CancelIcon />}
                        label="CONDITION FAILED"
                    />
                );
            case "AI_EXECUTE":
                return (
                    <Chip
                        size="small"
                        color="info"
                        icon={<SmartToyIcon />}
                        label="AI EXECUTE"
                    />
                );
            case "FAILED":
            default:
                return (
                    <Chip
                        size="small"
                        color="error"
                        icon={<ErrorOutlineIcon />}
                        label="FAILED"
                    />
                );
        }
    };

    // ✅ group by status
    const statusCounts = useMemo(() => {
        const acc = Object.fromEntries(STATUSES.map((s) => [s, 0]));
        let unknown = 0;

        for (const log of logs) {
            const s = log?.status;
            if (acc[s] !== undefined) acc[s] += 1;
            else unknown += 1;
        }

        return { ...acc, UNKNOWN: unknown };
    }, [logs]);

    const renderUser = (u) => {
        if (!u) {
            return (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    —
                </Typography>
            );
        }

        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>U</Avatar>
                <Box>
                    <Typography variant="body2">{`ID: ${u.id}`}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Telegram: {u.telegramId}
                    </Typography>
                </Box>
            </Stack>
        );
    };

    const renderError = (err) => {
        if (!err) {
            return (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    —
                </Typography>
            );
        }

        const text = String(err);
        const truncated = text.length > 120 ? text.slice(0, 120) + "…" : text;
        const withTooltip = text.length > 120;

        const content = (
            <Typography variant="body2" component="span">
                <code style={{ whiteSpace: "pre-wrap" }}>{truncated}</code>
            </Typography>
        );

        return withTooltip ? (
            <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{text}</span>} arrow>
                {content}
            </Tooltip>
        ) : (
            content
        );
    };

    return (
        <Card>
            <CardContent>
                <Stack spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">Логи выполнения</Typography>

                    {/* Всего + группировка по статусам */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            Всего: {logs.length}
                        </Typography>

                        <Chip
                            size="small"
                            color="success"
                            label={`SUCCESS: ${statusCounts.SUCCESS}`}
                        />
                        <Chip
                            size="small"
                            color="warning"
                            label={`COND FAILED: ${statusCounts.CHECK_CONDITION_FAILED}`}
                        />
                        <Chip
                            size="small"
                            color="info"
                            label={`AI_EXECUTE: ${statusCounts.AI_EXECUTE}`}
                        />
                        <Chip
                            size="small"
                            color="error"
                            label={`FAILED: ${statusCounts.FAILED}`}
                        />

                        {statusCounts.UNKNOWN > 0 && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`UNKNOWN: ${statusCounts.UNKNOWN}`}
                            />
                        )}
                    </Stack>
                </Stack>

                {logs.length === 0 ? (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Логи отсутствуют.
                    </Typography>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={80}>ID</TableCell>
                                    <TableCell width={160}>Статус</TableCell>
                                    <TableCell>Результат</TableCell>
                                    <TableCell width={260}>Пользователь</TableCell>
                                    <TableCell width={200}>Создано</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>{log.id}</TableCell>
                                        <TableCell>{statusChip(log.status)}</TableCell>
                                        <TableCell>{renderError(log.error)}</TableCell>
                                        <TableCell>{renderUser(log.user)}</TableCell>
                                        <TableCell>{fmtDate(log.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}